import React, { useEffect, useMemo } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import Select, { MultiValue } from 'react-select'

import { FileInput, Input, InputGroup } from '@components'
import Breadcrumb from '@components/Breadcrumb'
import Button from '@components/Button'
import PageTitle from '@components/PageTitle'
import Template from '@components/Template'
import { successMessages } from '@constants/feedbackMessages'
import { yupResolver } from '@hookform/resolvers/yup'
import { aiGenerationSchema } from '@schemas/aiGeneration.schema'
import { aiGenerationApi } from '@services/AiGenerationApi'
import { bancaApi } from '@services/BancaApi'
import { institutionApi } from '@services/InstitutionApi'
import { occupationApi } from '@services/OccupationApi'
import { yearApi } from '@services/YearApi'
import { notificationCreators } from '@store/ducks/notification'
import { useAppDispatch } from '@store/hooks'
import { describeGenerationError } from '@utils/helpers'
import { createBreadcrumbLink, useCreateBreadcrumb } from '@utils/hooks'

type Option = { value: number; label: string }

type Inputs = {
	examPdf: FileList
	answerKeyPdf: FileList
	answerKeyVariant?: string
	scopes?: string
	yearSelect?: MultiValue<Option>
	occupationSelect?: MultiValue<Option>
	bancaSelect?: MultiValue<Option>
	institutionSelect?: MultiValue<Option>
}

const selectStyles = {
	option: (
		base: Record<string, unknown>,
		{ isFocused }: { isFocused: boolean }
	) => ({
		...base,
		backgroundColor: isFocused ? '#1673FA' : '#fff',
		color: isFocused ? '#fff' : '#000',
	}),
}

function NewGeneration() {
	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	const baseUrl = '/conversoes'

	const allPages = useMemo(
		() => [
			createBreadcrumbLink('Conversão de provas', baseUrl, 1),
			createBreadcrumbLink('Nova conversão', `${baseUrl}/nova`, 2),
		],
		[]
	)

	const pages = useCreateBreadcrumb(allPages)

	const [createGeneration, { isSuccess, isLoading, data, error }] =
		aiGenerationApi.useCreateMutation()

	const { register, handleSubmit, setValue, control } = useForm<Inputs>({
		mode: 'onBlur',
		resolver: yupResolver(aiGenerationSchema),
	})

	useEffect(() => {
		if (isSuccess && data) {
			dispatch(notificationCreators.show(successMessages.startedAiGeneration))

			navigate(`${baseUrl}/${data.id}`)
		}
	}, [isSuccess, data, dispatch, navigate])

	const { data: dataYear } = yearApi.useListQuery({
		page: 1,
		limit: 100000,
		search: '',
	})
	const { data: dataOccupation } = occupationApi.useListQuery({
		page: 1,
		limit: 100000,
		search: '',
	})
	const { data: dataBanca } = bancaApi.useListQuery({
		page: 1,
		limit: 100000,
		search: '',
	})
	const { data: dataInstitution } = institutionApi.useListQuery({
		page: 1,
		limit: 100000,
		search: '',
	})

	const onSubmit: SubmitHandler<Inputs> = (form) => {
		// Os filtros escolhidos aqui valem para toda questão do caderno e vencem
		// o que a IA ler da capa da prova: quem escolheu sabe qual prova é, e a
		// leitura do PDF erra siglas.
		const filterIds = [
			...(form.yearSelect ?? []),
			...(form.occupationSelect ?? []),
			...(form.bancaSelect ?? []),
			...(form.institutionSelect ?? []),
		].map((option) => option.value)

		/*
		 * O escopo é digitado separado por vírgula porque não há cadastro dele:
		 * concurso existe em união, estado, município, tribunal e consórcio, e
		 * qualquer lista fechada marcaria de federal o que ela não previu — o que
		 * na busca quer dizer "concorre em toda prova do país".
		 */
		const scopes = (form.scopes ?? '')
			.split(',')
			.map((scope) => scope.trim())
			.filter((scope) => scope.length > 0)

		createGeneration({
			examPdf: form.examPdf,
			answerKeyPdf: form.answerKeyPdf,
			answerKeyVariant: form.answerKeyVariant,
			filterIds,
			scopes,
		})
	}

	const toOptions = (items?: { data?: Array<{ id: number; name: string }> }) =>
		items?.data?.map((item) => ({ value: item.id, label: item.name })) ?? []

	return (
		<Template>
			<Breadcrumb pages={pages} />
			<PageTitle title='Nova conversão de prova' />

			<p className='mt-2 mb-2 text-gray max-w-[820px] leading-relaxed'>
				Envie o caderno de provas e o gabarito oficial. A conversão lê as
				questões, busca a lei que decide cada uma e devolve tudo para a
				curadoria. As questões vão aparecendo conforme ficam prontas.
			</p>

			<form onSubmit={handleSubmit(onSubmit)} className='mt-6'>
				<div className='grid grid-cols-2 gap-4'>
					<Controller
						control={control}
						name='examPdf'
						render={({ field, fieldState }) => (
							<FileInput
								accept='application/pdf'
								onChange={(files) => field.onChange(files)}
								isValid={fieldState.isTouched ? !fieldState.error : undefined}
							>
								Caderno de provas (PDF)
							</FileInput>
						)}
					/>

					<Controller
						control={control}
						name='answerKeyPdf'
						render={({ field, fieldState }) => (
							<FileInput
								accept='application/pdf'
								onChange={(files) => field.onChange(files)}
								isValid={fieldState.isTouched ? !fieldState.error : undefined}
							>
								Gabarito oficial (PDF)
							</FileInput>
						)}
					/>
				</div>

				<div className='grid grid-cols-2 gap-4 mt-6'>
					<InputGroup
						label='Versão da prova'
						id='answerKeyVariant'
						hint='Quando a prova sai em mais de uma versão, escreva a que quer converter — ela escolhe tanto o caderno quanto o bloco do gabarito. Copie como está impresso ("VERSÃO 01", "Tipo 2"); a palavra pode variar, o número não. Versão única, deixe em branco.'
					>
						<Input
							type='text'
							placeholder='PROVA TIPO 1'
							{...register('answerKeyVariant')}
						/>
					</InputGroup>

					<InputGroup
						label='Abrangência da prova'
						id='scopes'
						hint='De quem é o concurso, separado por vírgula. Libera a lei estadual ou municipal a fundamentar a questão. Em branco, a conversão deduz do início do caderno, e valem só as normas nacionais se ela não achar nada.'
					>
						<Input
							type='text'
							placeholder='espírito santo, cariacica'
							{...register('scopes')}
						/>
					</InputGroup>

					<InputGroup label='Banca' id='bancaSelect'>
						<Controller
							control={control}
							name='bancaSelect'
							render={({ field: { value } }) => (
								<Select
									isMulti
									value={value || null}
									options={toOptions(dataBanca)}
									onChange={(selected) => setValue('bancaSelect', selected)}
									styles={selectStyles}
									placeholder=''
									classNamePrefix='react-select'
								/>
							)}
						/>
					</InputGroup>

					<InputGroup label='Ano' id='yearSelect'>
						<Controller
							control={control}
							name='yearSelect'
							render={({ field: { value } }) => (
								<Select
									isMulti
									value={value || null}
									options={toOptions(dataYear)}
									onChange={(selected) => setValue('yearSelect', selected)}
									styles={selectStyles}
									placeholder=''
									classNamePrefix='react-select'
								/>
							)}
						/>
					</InputGroup>

					<InputGroup label='Cargo' id='occupationSelect'>
						<Controller
							control={control}
							name='occupationSelect'
							render={({ field: { value } }) => (
								<Select
									isMulti
									value={value || null}
									options={toOptions(dataOccupation)}
									onChange={(selected) =>
										setValue('occupationSelect', selected)
									}
									styles={selectStyles}
									placeholder=''
									classNamePrefix='react-select'
								/>
							)}
						/>
					</InputGroup>

					<InputGroup label='Instituição' id='institutionSelect'>
						<Controller
							control={control}
							name='institutionSelect'
							render={({ field: { value } }) => (
								<Select
									isMulti
									value={value || null}
									options={toOptions(dataInstitution)}
									onChange={(selected) =>
										setValue('institutionSelect', selected)
									}
									styles={selectStyles}
									placeholder=''
									classNamePrefix='react-select'
								/>
							)}
						/>
					</InputGroup>
				</div>

				<p className='mt-6 text-sm text-gray max-w-[820px] leading-relaxed'>
					Os filtros valem para todas as questões do caderno e vencem o que a
					conversão ler da capa. Deixe em branco o que a prova já disser de si;
					preencha o que ela não trouxer, como o ano em provas que não o
					imprimem.
				</p>

				{error && (
					<p className='mt-4 text-red'>{describeGenerationError(error)}</p>
				)}

				<div className='flex gap-4 mt-6 mb-10'>
					<Button type='submit' disabled={isLoading}>
						{isLoading ? 'Enviando...' : 'Iniciar conversão'}
					</Button>
					<Button
						type='button'
						variant='outline'
						onClick={() => navigate(baseUrl)}
					>
						Cancelar
					</Button>
				</div>
			</form>
		</Template>
	)
}

export default NewGeneration
